import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { useTheme } from './theme';
import LangSwitcher from './LangSwitcher';

const API_URL = process.env.REACT_APP_API_URL ?? '';

/**
 * 👥 Taklif havolasi sahifasi — /invite/:token (login'siz ochiladi).
 * Hisobi yo'q odam shu yerda hisob yaratadi va loyihaga qo'shiladi; hisobi bor va kirgan
 * odam bitta tugma bilan qo'shiladi. Server: routes/team.js.
 */
export default function InvitePage() {
  const { token } = useParams();
  const { token: authToken, user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '', password2: '' });
  const [theme, toggleTheme] = useTheme();

  useEffect(() => {
    fetch(`${API_URL}/invites/${encodeURIComponent(token)}`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (ok) setInfo(d); else setErr(d.error || 'Taklif ochilmadi'); })
      .catch(() => setErr("Server bilan bog'lanib bo'lmadi"));
  }, [token]);

  const goProject = (projectId) => {
    // Studio shu loyihani ochadi (App ro'yxatdan birinchisini tanlaydi — shuning uchun eslatib qo'yamiz).
    try { if (projectId) localStorage.setItem('kh_m_project', String(projectId)); } catch (e) {}
    navigate('/');
  };

  const join = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/invites/${encodeURIComponent(token)}/join`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const d = await res.json().catch(() => ({}));
      if (res.status === 401) { logout(); toast.error('Sessiya eskirgan — qayta kiring yoki yangi hisob yarating'); return; }
      if (!res.ok) { toast.error(d.error || 'Qo‘shilib bo‘lmadi'); return; }
      toast.success(d.alreadyMember ? 'Siz allaqachon shu loyihadasiz' : `✅ «${info.projectName}» loyihasiga qo'shildingiz`);
      goProject(d.projectId);
    } catch { toast.error("Server bilan bog'lanib bo'lmadi"); }
    finally { setBusy(false); }
  };

  const accept = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error('Parollar mos emas'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/invites/${encodeURIComponent(token)}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.username.trim(), email: form.email.trim(), password: form.password }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.error || 'Hisob yaratilmadi'); return; }
      login(d.token, d.user);
      toast.success(`Xush kelibsiz, ${d.user.username}!`);
      goProject(d.projectId);
    } catch { toast.error("Server bilan bog'lanib bo'lmadi"); }
    finally { setBusy(false); }
  };

  const inputCls = 'bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white sm:text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 focus:ring-2 focus:outline-none block w-full p-2.5';
  const labelCls = 'block mb-2 text-sm font-medium text-gray-900 dark:text-white';
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen px-6 pt-8 pb-8 bg-gray-50 dark:bg-gray-900">
      <div className="absolute top-4 right-4 flex items-center gap-1">
        <LangSwitcher up={false} />
        <button type="button" onClick={() => toggleTheme()} title={theme === 'dark' ? 'Kunduzgi rejim' : 'Tungi rejim'}
          className="p-2 text-lg leading-none text-gray-500 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
      </div>
      <div className="flex items-center justify-center mb-8 text-2xl font-semibold text-gray-900 dark:text-white">
        <img src="/logo192.png" alt="" className="kh-brand-logo w-9 h-9 mr-3 rounded-lg object-cover shadow-sm" />
        <span>Khorezmai</span>
      </div>
      <div className="w-full max-w-md p-6 space-y-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm sm:p-8">
        {err && (
          <>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Taklif ochilmadi</h2>
            <p className="text-sm text-red-600 dark:text-red-400">{err}</p>
            <button onClick={() => navigate('/login')} className="text-sm text-primary-700 dark:text-primary-400 hover:underline">Kirish sahifasiga o'tish</button>
          </>
        )}
        {!err && !info && <p className="text-sm text-gray-500">⏳ Yuklanmoqda…</p>}
        {info && (
          <>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">👥 Jamoaga taklif</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {info.inviter ? <><b translate="no">{info.inviter}</b> sizni </> : 'Sizni '}
                <b translate="no">«{info.projectName}»</b> loyihasiga <b>{info.roleLabel}</b> sifatida taklif qildi.
              </p>
            </div>
            {authToken && user ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">Siz <b translate="no">{user.username}</b> sifatida kirgansiz.</p>
                <button onClick={join} disabled={busy}
                  className="w-full text-white bg-primary-700 hover:bg-primary-800 font-medium rounded-lg text-sm px-5 py-2.5 disabled:opacity-50">
                  {busy ? '⏳' : 'Loyihaga qo‘shilish'}
                </button>
                <button onClick={() => logout()} className="w-full text-sm text-gray-500 dark:text-gray-400 hover:underline">Boshqa hisob bilan (chiqish)</button>
              </div>
            ) : (
              <form onSubmit={accept} className="space-y-4">
                <div><label className={labelCls}>Foydalanuvchi nomi</label>
                  <input className={inputCls} value={form.username} onChange={set('username')} autoComplete="username" required placeholder="masalan: ali" /></div>
                <div><label className={labelCls}>Email</label>
                  <input className={inputCls} type="email" value={form.email} onChange={set('email')} autoComplete="email" required /></div>
                <div><label className={labelCls}>Parol (kamida 8 belgi)</label>
                  <input className={inputCls} type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required minLength={8} /></div>
                <div><label className={labelCls}>Parolni takrorlang</label>
                  <input className={inputCls} type="password" value={form.password2} onChange={set('password2')} autoComplete="new-password" required minLength={8} /></div>
                <button type="submit" disabled={busy}
                  className="w-full text-white bg-primary-700 hover:bg-primary-800 font-medium rounded-lg text-sm px-5 py-2.5 disabled:opacity-50">
                  {busy ? '⏳' : 'Hisob yaratish va qo‘shilish'}
                </button>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Hisobingiz bormi? <button type="button" onClick={() => navigate(`/login?next=${encodeURIComponent('/invite/' + token)}`)} className="text-primary-700 dark:text-primary-400 hover:underline">Kiring</button>, keyin shu havolani qayta oching.
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
